import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Badge,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {
    declaredDnsGrant,
    ProviderConnection,
} from '../types';
import {capabilitySnapshotCaption} from '../statusPresentation';

// FNP-1248 — WHAT THIS CREDENTIAL CAN DO.
//
// ⚠️ A READ-ONLY CREDENTIAL IS A WORKING CREDENTIAL. The single most important
// presentation decision in this component.
//
// The catalog says Route 53 canWriteRecords: true. A tenant who deliberately
// handed Flexi a READ-ONLY role has a connection that cannot write, and that is
// a correct, intentional, fully-functional setup — not a fault. Rendering those
// capabilities as red crosses, or as an "incomplete" state with a warning, tells
// a careful tenant that their careful choice is broken.
//
// So capabilities render as PRESENCE and ABSENCE ("can create zones" /
// "cannot create zones"), in neutral tones, with no remediation prompt. The only
// thing coloured red in this module is an actual failure.

/**
 * FNP-1399 — name the grants the tenant actually made.
 *
 * ⚠️ IT LISTS WHAT WAS GRANTED RATHER THAN SUMMARISING IT. The badge this
 * replaced said "This role can create and delete zones" whenever ANY write
 * grant existed, so a customer who granted only record writes read that Flexi
 * could delete their hosted zones. Saying less than the truth is safer than
 * saying more, but saying the wrong thing is worse than either.
 *
 * The caller only renders this when at least one grant is true, so the empty
 * case cannot be reached from there; it still returns the read-only wording
 * rather than an empty badge, because a badge with no text is a rendering bug
 * wearing a capability's clothes.
 */
const declaredGrantSummary = (
    records: boolean,
    create: boolean,
    remove: boolean,
): string => {
    const granted: string[] = [];
    if (records) {
        granted.push(I18n.get('change records'));
    }
    if (create) {
        granted.push(I18n.get('create zones'));
    }
    if (remove) {
        granted.push(I18n.get('delete zones'));
    }
    if (granted.length === 0) {
        return I18n.get('Read-only');
    }
    return `${I18n.get('This role can')} ${granted.join(', ')}`;
};

const CapabilityRow = (
    {
        label,
        granted,
    }: {
        label: string,
        granted: boolean,
    },
) => (
    <Group gap={'xs'} justify={'space-between'} wrap={'nowrap'}>
        <Text size={'sm'}>{label}</Text>
        {/* ⚠️ 'gray' for absence, NOT 'red'. Absence is a statement about the
            credential the tenant chose to supply, not an error in it. */}
        <Badge
            variant={'light'}
            color={granted ? 'green' : 'gray'}
            size={'sm'}
        >
            {granted ? I18n.get('Yes') : I18n.get('No')}
        </Badge>
    </Group>
);

export const CapabilitySnapshot = (
    {connection}: { connection: ProviderConnection },
) => {

    const capabilities = connection.capabilities;

    // `capabilities` is non-null in the schema, but a partial/degraded response
    // must not throw inside a render.
    if (!capabilities) {
        return (
            <Text size={'sm'} c={'dimmed'}>
                {productCopy('{product} has no capability snapshot for this connection.')}
            </Text>
        );
    }

    const recordTypes = capabilities.supportedRecordTypes ?? [];

    // ⚠️ FNP-1233, split by FNP-1399 — the DECLARED grants, read through the
    // fail-closed helper. See types.ts: an absent block or field resolves to
    // NOT granted, never to granted-by-omission.
    //
    // ⚠️ THREE BADGES, NOT ONE. This used to be a single badge reading "This
    // role can create and delete zones", driven by one boolean that was the OR
    // of all three grants — so a customer who granted only record writes was
    // told, on their own connection page, that Flexi could delete their zones.
    // The whole point of storing the grants separately is that the product can
    // now say which ones it actually holds.
    const declaredRecords = declaredDnsGrant(connection, 'canWriteRecords');
    const declaredCreate = declaredDnsGrant(connection, 'canCreateZones');
    const declaredDelete = declaredDnsGrant(connection, 'canDeleteZones');
    const declaredAny = declaredRecords || declaredCreate || declaredDelete;

    return (
        <Stack gap={'sm'}>

            {/* ⚠️ The caption changes tense on a non-ACTIVE connection. A
                snapshot records what the credential proved AT CONNECT TIME; next
                to a broken status, presenting it as current fact would claim the
                connection can do things while nothing works at all. */}
            <Text size={'xs'} c={'dimmed'} fw={500}>
                {capabilitySnapshotCaption(connection)}
            </Text>

            {/* ─── FNP-1233: THE TENANT'S DECLARATION ───
                Shown first because it is what makes the rest meaningful: with
                writes undeclared, the write rows below are uniformly No, and a
                reader with no explanation would think the credential failed. */}
            <Group gap={'xs'} justify={'space-between'} wrap={'nowrap'}>
                <Text size={'sm'}>{I18n.get('Declared to write')}</Text>
                <Badge
                    variant={'light'}
                    color={declaredAny ? 'green' : 'gray'}
                    size={'sm'}
                    data-testid={'writable-declared'}
                >
                    {declaredAny
                        ? declaredGrantSummary(
                            declaredRecords,
                            declaredCreate,
                            declaredDelete,
                        )
                        : I18n.get('Read-only')}
                </Badge>
            </Group>

            <CapabilityRow
                label={I18n.get('Write records')}
                granted={capabilities.canWriteRecords}
            />
            <CapabilityRow
                label={I18n.get('Create zones')}
                granted={capabilities.canCreateZones}
            />
            <CapabilityRow
                label={I18n.get('Delete zones')}
                granted={capabilities.canDeleteZones}
            />
            <CapabilityRow
                label={I18n.get('Set nameservers')}
                granted={capabilities.canSetNameservers}
            />

            <Stack gap={4}>
                <Text size={'sm'}>{I18n.get('Record types')}</Text>
                {recordTypes.length > 0 ? (
                    <Group gap={4}>
                        {recordTypes.map(recordType => (
                            <Badge
                                key={recordType}
                                variant={'outline'}
                                color={'gray'}
                                size={'sm'}
                            >
                                {recordType}
                            </Badge>
                        ))}
                    </Group>
                ) : (
                    // Distinct from "none supported": an empty list here means
                    // the snapshot did not enumerate them.
                    <Text size={'xs'} c={'dimmed'}>
                        {I18n.get('Not enumerated for this connection')}
                    </Text>
                )}
            </Stack>

        </Stack>
    );
};
